/**
 * React Query hooks for caching API data
 * These hooks provide automatic caching, refetching, and invalidation
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions, QueryKey } from 'react-query';
import { 
  DonationsAPI, Donation, DonationSearchParams,
  DonorsAPI, Donor,
  GuestDonorsAPI, GuestDonor,
  ExpensesAPI, ExpenseSearchParams,
  AccountsAPI, Account, AccountSearchParams,
  TransactionsAPI, Transaction, TransactionSearchParams,
  OfficesAPI, Office,
  RegionsAPI, Region,
  ControllersAPI, Controller,
  DonationTypesAPI, DonationType,
  ExpenseTypesAPI, ExpenseType,
  DashboardAPI, DashboardData,
  AuditLogsAPI, AuditLog, AuditLogFilters,
  IncomeDepartmentsAPI, IncomeDepartment, CreateIncomeDepartmentData, UpdateIncomeDepartmentData,
  IncomeTypesAPI, IncomeType, CreateIncomeTypeData, UpdateIncomeTypeData,
  IncomesAPI, IncomeSearchParams,
} from '../lib/sdk/index';
import { UsersAPI, User, ListUsersParams } from '../lib/sdk/users';

// Query Keys
export const queryKeys = {
  donations: (params?: DonationSearchParams): QueryKey => ['donations', params],
  donation: (id: string): QueryKey => ['donation', id],
  donationStats: (params?: any): QueryKey => ['donationStats', params],
  donors: (params?: { page?: number; limit?: number; search?: string; officeId?: string }): QueryKey => ['donors', params],
  donor: (id: string): QueryKey => ['donor', id],
  guestDonors: (params?: { page?: number; limit?: number; search?: string; officeId?: string }): QueryKey => ['guestDonors', params],
  guestDonor: (id: string): QueryKey => ['guestDonor', id],
  expenses: (params?: ExpenseSearchParams): QueryKey => ['expenses', params],
  expense: (id: string): QueryKey => ['expense', id],
  expenseStats: (params?: any): QueryKey => ['expenseStats', params],
  accounts: (params?: AccountSearchParams): QueryKey => ['accounts', params],
  account: (id: string): QueryKey => ['account', id],
  activeAccounts: (): QueryKey => ['activeAccounts'],
  transactions: (params?: TransactionSearchParams): QueryKey => ['transactions', params],
  recentTransactions: (limit?: number): QueryKey => ['recentTransactions', limit],
  transaction: (id: string): QueryKey => ['transaction', id],
  offices: (params?: { page?: number; limit?: number; search?: string }): QueryKey => ['offices', params],
  office: (id: string): QueryKey => ['office', id],
  regions: (params?: { page?: number; limit?: number; search?: string }): QueryKey => ['regions', params],
  region: (id: string): QueryKey => ['region', id],
  controllers: (params?: { page?: number; limit?: number; search?: string }): QueryKey => ['controllers', params],
  controller: (id: string): QueryKey => ['controller', id],
  donationTypes: (): QueryKey => ['donationTypes'],
  donationType: (id: string): QueryKey => ['donationType', id],
  expenseTypes: (): QueryKey => ['expenseTypes', 'active'],
  expenseTypesAll: (): QueryKey => ['expenseTypes', 'all'],
  expenseType: (id: string): QueryKey => ['expenseType', id],
  dashboard: (period?: string): QueryKey => ['dashboard', period],
  users: (params?: ListUsersParams): QueryKey => ['users', params],
  user: (id: string): QueryKey => ['user', id],
  auditLogs: (filters?: AuditLogFilters): QueryKey => ['auditLogs', filters],
  auditLog: (id: string): QueryKey => ['auditLog', id],
  auditLogStats: (startDate?: string, endDate?: string): QueryKey => ['auditLogStats', startDate, endDate],
  incomeDepartments: (): QueryKey => ['incomeDepartments', 'all'],
  activeIncomeDepartments: (): QueryKey => ['incomeDepartments', 'active'],
  incomeTypes: (): QueryKey => ['incomeTypes', 'active'],
  incomeTypesAll: (): QueryKey => ['incomeTypes', 'all'],
  incomes: (params?: IncomeSearchParams): QueryKey => ['incomes', params],
};

// Donations Hooks
export const useDonations = (params: DonationSearchParams = {}, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.donations(params),
    () => DonationsAPI.getAllDonations(params),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      ...options,
    }
  );
};

export const useDonation = (id: string, options?: Omit<UseQueryOptions<Donation, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.donation(id),
    () => DonationsAPI.getDonationById(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

export const useDonationStats = (params?: any, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.donationStats(params),
    () => DonationsAPI.getDonationStats(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes for stats
      ...options,
    }
  );
};

export const useCreateDonation = (options?: UseMutationOptions<Donation, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => DonationsAPI.createDonation(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['donations']);
        queryClient.invalidateQueries(['donationStats']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.refetchQueries(['donations'], { active: true });
        queryClient.refetchQueries(['donationStats'], { active: true });
        queryClient.refetchQueries(['dashboard'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateDonation = (options?: UseMutationOptions<Donation, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => DonationsAPI.updateDonation(id, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['donations']);
        queryClient.invalidateQueries(queryKeys.donation(variables.id));
        queryClient.invalidateQueries(['donationStats']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.refetchQueries(['donations'], { active: true });
        queryClient.refetchQueries(['donationStats'], { active: true });
        queryClient.refetchQueries(['dashboard'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteDonation = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => DonationsAPI.deleteDonation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['donations']);
        queryClient.invalidateQueries(['donationStats']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.refetchQueries(['donations'], { active: true });
        queryClient.refetchQueries(['donationStats'], { active: true });
        queryClient.refetchQueries(['dashboard'], { active: true });
      },
      ...options,
    }
  );
};

// Donors Hooks
export const useDonors = (params?: { page?: number; limit?: number; search?: string; officeId?: string }, options?: Omit<UseQueryOptions<{ data: Donor[]; pagination?: any }, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.donors(params),
    () => DonorsAPI.getAllDonors(params || { page: 1, limit: 50 }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      keepPreviousData: true, // Keep previous data while fetching new page
      ...options,
    }
  );
};

export const useCreateDonor = (options?: UseMutationOptions<Donor, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => DonorsAPI.createDonor(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['donors']);
        queryClient.refetchQueries(['donors'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateDonor = (options?: UseMutationOptions<Donor, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => DonorsAPI.updateDonor(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.donors());
      },
      ...options,
    }
  );
};

export const useDeleteDonor = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => DonorsAPI.deleteDonor(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['donors']);
        queryClient.refetchQueries(['donors'], { active: true });
      },
      ...options,
    }
  );
};

// Guest Donors Hooks
export const useGuestDonors = (params?: { page?: number; limit?: number; search?: string; officeId?: string }, options?: Omit<UseQueryOptions<{ data: GuestDonor[]; pagination?: any }, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.guestDonors(params),
    () => GuestDonorsAPI.getAllGuestDonors(params || { page: 1, limit: 50 }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      keepPreviousData: true, // Keep previous data while fetching new page
      ...options,
    }
  );
};

export const useCreateGuestDonor = (options?: UseMutationOptions<GuestDonor, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => GuestDonorsAPI.createGuestDonor(data),
    {
      onSuccess: () => {
        // Invalidate all guestDonors queries (all pagination variations)
        queryClient.invalidateQueries(['guestDonors']);
        // Refetch all active guestDonors queries
        queryClient.refetchQueries(['guestDonors'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateGuestDonor = (options?: UseMutationOptions<GuestDonor, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => GuestDonorsAPI.updateGuestDonor(id, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['guestDonors']);
        queryClient.invalidateQueries(queryKeys.guestDonor(variables.id));
        queryClient.refetchQueries(['guestDonors'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteGuestDonor = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => GuestDonorsAPI.deleteGuestDonor(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['guestDonors']);
        queryClient.refetchQueries(['guestDonors'], { active: true });
      },
      ...options,
    }
  );
};

export const useConvertGuestDonorToPermanent = (options?: UseMutationOptions<any, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => GuestDonorsAPI.convertGuestDonorToPermanent(data),
    {
      onSuccess: () => {
        // Invalidate both guest donors and regular donors since a guest donor was converted
        queryClient.invalidateQueries(['guestDonors']);
        queryClient.invalidateQueries(['donors']);
        // Refetch all active queries
        queryClient.refetchQueries(['guestDonors'], { active: true });
        queryClient.refetchQueries(['donors'], { active: true });
      },
      ...options,
    }
  );
};

// Expenses Hooks
export const useExpenses = (params: ExpenseSearchParams = {}, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.expenses(params),
    () => ExpensesAPI.getAllExpenses(params),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      ...options,
    }
  );
};

export const useCreateExpense = (options?: UseMutationOptions<any, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => ExpensesAPI.createExpense(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenses']);
        queryClient.invalidateQueries(['expenseStats']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.refetchQueries(['expenses'], { active: true });
        queryClient.refetchQueries(['expenseStats'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['transactions'], { active: true });
        queryClient.refetchQueries(['recentTransactions'], { active: true });
        queryClient.refetchQueries(['dashboard'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateExpense = (options?: UseMutationOptions<any, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => ExpensesAPI.updateExpense(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenses']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.refetchQueries(['expenses'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['transactions'], { active: true });
        queryClient.refetchQueries(['recentTransactions'], { active: true });
        queryClient.refetchQueries(['dashboard'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteExpense = (options?: UseMutationOptions<any, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ExpensesAPI.deleteExpense(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenses']);
        queryClient.invalidateQueries(['expenseStats']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.refetchQueries(['expenses'], { active: true });
        queryClient.refetchQueries(['expenseStats'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['transactions'], { active: true });
        queryClient.refetchQueries(['recentTransactions'], { active: true });
        queryClient.refetchQueries(['dashboard'], { active: true });
      },
      ...options,
    }
  );
};

export const useExpenseStats = (params: { startDate?: string; endDate?: string; accountId?: string } = {}, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.expenseStats(params),
    () => ExpensesAPI.getExpenseStats(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes for stats
      cacheTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

// Accounts Hooks
export const useAccounts = (params: AccountSearchParams = {}, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.accounts(params),
    () => AccountsAPI.getAllAccounts(params),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      ...options,
    }
  );
};

export const useActiveAccounts = (options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.activeAccounts(),
    () => AccountsAPI.getActiveAccounts(),
    {
      staleTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

export const useCreateAccount = (options?: UseMutationOptions<Account, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => AccountsAPI.createAccount(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['activeAccounts']);
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['activeAccounts'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateAccount = (options?: UseMutationOptions<Account, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => AccountsAPI.updateAccount(id, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(queryKeys.account(variables.id));
        queryClient.invalidateQueries(['activeAccounts']);
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['activeAccounts'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteAccount = (options?: UseMutationOptions<{ success: boolean; message: string }, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => AccountsAPI.deleteAccount(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['activeAccounts']);
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['activeAccounts'], { active: true });
      },
      ...options,
    }
  );
};

// Transactions Hooks
export const useTransactions = (params: TransactionSearchParams = {}, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.transactions(params),
    () => TransactionsAPI.getAllTransactions(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes for transactions
      cacheTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

export const useRecentTransactions = (limit: number = 50, options?: Omit<UseQueryOptions<Transaction[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.recentTransactions(limit),
    () => TransactionsAPI.getRecentTransactions(limit),
    {
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

export const useCreateTransaction = (options?: UseMutationOptions<Transaction, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => TransactionsAPI.createTransaction(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['activeAccounts']);
        queryClient.refetchQueries(['transactions'], { active: true });
        queryClient.refetchQueries(['recentTransactions'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['activeAccounts'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateTransaction = (options?: UseMutationOptions<Transaction, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => TransactionsAPI.updateTransaction(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['activeAccounts']);
        queryClient.refetchQueries(['transactions'], { active: true });
        queryClient.refetchQueries(['recentTransactions'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['activeAccounts'], { active: true });
      },
      ...options,
    }
  );
};

// Offices Hooks
export const useOffices = (params?: { page?: number; limit?: number; search?: string }, options?: Omit<UseQueryOptions<{ data: Office[]; pagination?: any }, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.offices(params),
    () => OfficesAPI.getAllOffices(params || { page: 1, limit: 50 }),
    {
      staleTime: 15 * 60 * 1000, // 15 minutes for master data (longer cache)
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      keepPreviousData: true, // Keep previous data while fetching new page
      ...options,
    }
  );
};

export const useCreateOffice = (options?: UseMutationOptions<Office, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => OfficesAPI.createOffice(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['offices']);
        queryClient.refetchQueries(['offices'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateOffice = (options?: UseMutationOptions<Office, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => OfficesAPI.updateOffice(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['offices']);
        queryClient.refetchQueries(['offices'], { active: true });
      },
      ...options,
    }
  );
};

// Regions Hooks
export const useRegions = (params?: { page?: number; limit?: number; search?: string }, options?: Omit<UseQueryOptions<{ data: Region[]; pagination?: any }, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.regions(params),
    () => RegionsAPI.getAllRegions(params || { page: 1, limit: 50 }),
    {
      staleTime: 15 * 60 * 1000, // 15 minutes for master data
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      keepPreviousData: true, // Keep previous data while fetching new page
      ...options,
    }
  );
};

export const useCreateRegion = (options?: UseMutationOptions<Region, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => RegionsAPI.createRegion(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['regions']);
        queryClient.refetchQueries(['regions'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateRegion = (options?: UseMutationOptions<Region, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => RegionsAPI.updateRegion(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['regions']);
        queryClient.refetchQueries(['regions'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteRegion = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => RegionsAPI.deleteRegion(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['regions']);
        queryClient.refetchQueries(['regions'], { active: true });
      },
      ...options,
    }
  );
};

// Controllers Hooks
export const useControllers = (params?: { page?: number; limit?: number; search?: string }, options?: Omit<UseQueryOptions<{ data: Controller[]; pagination?: any }, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.controllers(params),
    () => ControllersAPI.getAllControllers(params || { page: 1, limit: 50 }),
    {
      staleTime: 15 * 60 * 1000, // 15 minutes for master data
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      keepPreviousData: true, // Keep previous data while fetching new page
      ...options,
    }
  );
};

export const useCreateController = (options?: UseMutationOptions<Controller, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => ControllersAPI.createController(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['controllers']);
        queryClient.refetchQueries(['controllers'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateController = (options?: UseMutationOptions<Controller, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => ControllersAPI.updateController(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['controllers']);
        queryClient.refetchQueries(['controllers'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteController = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ControllersAPI.deleteController(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['controllers']);
        queryClient.refetchQueries(['controllers'], { active: true });
      },
      ...options,
    }
  );
};

// Donation Types Hooks
export const useDonationTypes = (options?: Omit<UseQueryOptions<DonationType[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.donationTypes(),
    () => DonationTypesAPI.getAllDonationTypes(),
    {
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      ...options,
    }
  );
};

// Expense Types Hooks — active types only (add/manage expense forms; no manageExpenseTypes required)
export const useExpenseTypes = (options?: Omit<UseQueryOptions<ExpenseType[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.expenseTypes(),
    () => ExpenseTypesAPI.getActiveExpenseTypes(),
    {
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      ...options,
    }
  );
};

/** Full list including inactive — Expense Types admin page only */
export const useAllExpenseTypes = (options?: Omit<UseQueryOptions<ExpenseType[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.expenseTypesAll(),
    () => ExpenseTypesAPI.getAllExpenseTypes(),
    {
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      ...options,
    }
  );
};

export const useCreateExpenseType = (options?: UseMutationOptions<any, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => ExpenseTypesAPI.createExpenseType(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenseTypes']);
        queryClient.refetchQueries(['expenseTypes'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateExpenseType = (options?: UseMutationOptions<any, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => ExpenseTypesAPI.updateExpenseType(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenseTypes']);
        queryClient.refetchQueries(['expenseTypes'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteExpenseType = (options?: UseMutationOptions<any, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ExpenseTypesAPI.deleteExpenseType(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenseTypes']);
        queryClient.refetchQueries(['expenseTypes'], { active: true });
      },
      ...options,
    }
  );
};

// Dashboard Hooks
export const useDashboard = (period: string = '30', options?: Omit<UseQueryOptions<DashboardData, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.dashboard(period),
    () => DashboardAPI.getDashboard(period),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes - dashboard data doesn't change frequently
      cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
      refetchOnWindowFocus: false, // Don't refetch on window focus for dashboard
      refetchOnMount: true, // Only refetch if data is stale
      ...options,
    }
  );
};

// Users Hooks
export const useUsers = (params: ListUsersParams = {}, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.users(params),
    async () => {
      const response = await UsersAPI.list(params);
      // Return in format: { users: [], pagination: {} }
      return {
        users: response.users || [],
        pagination: response.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 }
      };
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      ...options,
    }
  );
};

export const useUser = (id: string, options?: Omit<UseQueryOptions<User, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.user(id),
    () => UsersAPI.get(id).then(res => res.user),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

export const useCreateUser = (options?: UseMutationOptions<User, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => UsersAPI.create(data).then(res => res.user),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.users(), { exact: false });
        queryClient.refetchQueries(queryKeys.users(), { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateUser = (options?: UseMutationOptions<User, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => UsersAPI.update(id, data).then(res => res.user),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(queryKeys.user(variables.id));
        queryClient.refetchQueries(['users'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteUser = (options?: UseMutationOptions<void, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => UsersAPI.delete(id).then(() => undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        queryClient.refetchQueries(['users'], { active: true });
      },
      ...options,
    }
  );
};

// Audit Logs Hooks
export const useAuditLogs = (filters?: AuditLogFilters, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.auditLogs(filters),
    () => AuditLogsAPI.getAllAuditLogs(filters),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes - audit logs change frequently
      cacheTime: 5 * 60 * 1000, // 5 minutes
      enabled: true, // Only fetch if user is SuperAdmin (handled in component)
      ...options,
    }
  );
};

export const useAuditLog = (id: string, options?: Omit<UseQueryOptions<AuditLog, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.auditLog(id),
    () => AuditLogsAPI.getAuditLogById(id).then(res => res.data),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

export const useAuditLogStats = (startDate?: string, endDate?: string, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.auditLogStats(startDate, endDate),
    () => AuditLogsAPI.getAuditLogStats(startDate, endDate).then(res => res.data),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes for stats
      ...options,
    }
  );
};

// ─── Income Departments Hooks ────────────────────────────────────────────────

export const useAllIncomeDepartments = (options?: Omit<UseQueryOptions<IncomeDepartment[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.incomeDepartments(),
    () => IncomeDepartmentsAPI.getAllIncomeDepartments(),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, ...options }
  );
};

export const useActiveIncomeDepartments = (options?: Omit<UseQueryOptions<IncomeDepartment[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.activeIncomeDepartments(),
    () => IncomeDepartmentsAPI.getActiveIncomeDepartments(),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, ...options }
  );
};

export const useCreateIncomeDepartment = (options?: UseMutationOptions<any, Error, CreateIncomeDepartmentData>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateIncomeDepartmentData) => IncomeDepartmentsAPI.createIncomeDepartment(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomeDepartments']);
        queryClient.refetchQueries(['incomeDepartments'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateIncomeDepartment = (options?: UseMutationOptions<any, Error, { id: string; data: UpdateIncomeDepartmentData }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: UpdateIncomeDepartmentData }) =>
      IncomeDepartmentsAPI.updateIncomeDepartment(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomeDepartments']);
        queryClient.refetchQueries(['incomeDepartments'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteIncomeDepartment = (options?: UseMutationOptions<any, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => IncomeDepartmentsAPI.deleteIncomeDepartment(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomeDepartments']);
        queryClient.refetchQueries(['incomeDepartments'], { active: true });
      },
      ...options,
    }
  );
};

// ─── Income Types Hooks ──────────────────────────────────────────────────────

export const useAllIncomeTypes = (options?: Omit<UseQueryOptions<IncomeType[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.incomeTypesAll(),
    () => IncomeTypesAPI.getAllIncomeTypes(),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, ...options }
  );
};

export const useActiveIncomeTypes = (options?: Omit<UseQueryOptions<IncomeType[], Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.incomeTypes(),
    () => IncomeTypesAPI.getActiveIncomeTypes(),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, ...options }
  );
};

export const useCreateIncomeType = (options?: UseMutationOptions<any, Error, CreateIncomeTypeData>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateIncomeTypeData) => IncomeTypesAPI.createIncomeType(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomeTypes']);
        queryClient.refetchQueries(['incomeTypes'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateIncomeType = (options?: UseMutationOptions<any, Error, { id: string; data: UpdateIncomeTypeData }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: UpdateIncomeTypeData }) =>
      IncomeTypesAPI.updateIncomeType(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomeTypes']);
        queryClient.refetchQueries(['incomeTypes'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteIncomeType = (options?: UseMutationOptions<any, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => IncomeTypesAPI.deleteIncomeType(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomeTypes']);
        queryClient.refetchQueries(['incomeTypes'], { active: true });
      },
      ...options,
    }
  );
};

// ─── Incomes Hooks ───────────────────────────────────────────────────────────

export const useIncomes = (params: IncomeSearchParams = {}, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery(
    queryKeys.incomes(params),
    () => IncomesAPI.getAllIncomes(params),
    { staleTime: 3 * 60 * 1000, cacheTime: 10 * 60 * 1000, ...options }
  );
};

export const useCreateIncome = (options?: UseMutationOptions<any, Error, any>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: any) => IncomesAPI.createIncome(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomes']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.refetchQueries(['incomes'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
        queryClient.refetchQueries(['transactions'], { active: true });
        queryClient.refetchQueries(['recentTransactions'], { active: true });
        queryClient.refetchQueries(['dashboard'], { active: true });
      },
      ...options,
    }
  );
};

export const useUpdateIncome = (options?: UseMutationOptions<any, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => IncomesAPI.updateIncome(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomes']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.refetchQueries(['incomes'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
      },
      ...options,
    }
  );
};

export const useDeleteIncome = (options?: UseMutationOptions<any, Error, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => IncomesAPI.deleteIncome(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['incomes']);
        queryClient.invalidateQueries(['accounts']);
        queryClient.invalidateQueries(['transactions']);
        queryClient.invalidateQueries(['recentTransactions']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.refetchQueries(['incomes'], { active: true });
        queryClient.refetchQueries(['accounts'], { active: true });
      },
      ...options,
    }
  );
};
